import { useQuery } from '@tanstack/react-query';
import { EmptyState, ListGroup, ListRow, SearchField, Skeleton } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { DOMAIN_STATUS, FilterChips, Pager, providerName, StatusBadge, useDebounced } from './common.tsx';

type DomainFilter = 'all' | 'pending' | 'active' | 'expired' | 'failed';

const FILTERS: { value: DomainFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'active', label: 'Активны' },
  { value: 'pending', label: 'Регистрируются' },
  { value: 'expired', label: 'Истекли' },
  { value: 'failed', label: 'Ошибки' },
];

const fullName = (user: { firstName: string; lastName: string | null }) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ');

export function AdminDomainsScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const { date, dateTime } = usePrefs();
  const [status, setStatus] = useState<DomainFilter>('all');
  const [provider, setProvider] = useState('all');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const query = useDebounced(search.trim());
  const domains = useQuery({
    ...trpc.admin.domains.queryOptions({
      status: status === 'all' ? undefined : status,
      provider: provider === 'all' ? undefined : provider,
      search: query,
      offset,
    }),
    placeholderData: (previous) => previous,
  });
  const providerFilters = [
    { value: 'all', label: 'Все поставщики' },
    ...(domains.data?.providers ?? []).map((value) => ({ value, label: providerName(value) })),
  ];

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Домены{domains.data ? ` · ${domains.data.total}` : ''}</h1>
      <SearchField
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setOffset(0);
        }}
        placeholder="Домен, пользователь или Telegram ID"
      />
      <FilterChips
        options={FILTERS}
        value={status}
        onChange={(value) => {
          setStatus(value);
          setOffset(0);
        }}
      />
      <FilterChips
        options={providerFilters}
        value={provider}
        onChange={(value) => {
          setProvider(value);
          setOffset(0);
        }}
      />

      {domains.isPending ? (
        <Skeleton height={320} />
      ) : domains.isError ? (
        <div className="ul-error" role="alert">
          {errorText(domains.error)}
        </div>
      ) : domains.data.rows.length === 0 ? (
        <EmptyState icon="globe" title="Доменов нет" text="С такими фильтрами ничего не найдено" />
      ) : (
        <div className={domains.isPlaceholderData ? 'ul-results ul-results--stale' : 'ul-results'}>
          <div className="ul-multiline">
            <ListGroup header={`Найдено ${domains.data.total}`}>
              {domains.data.rows.map((domain) => (
                <ListRow
                  key={domain.id}
                  icon="globe"
                  tone={
                    domain.status === 'failed' ? 'crit' : domain.status === 'expired' ? 'warn' : undefined
                  }
                  title={domain.name}
                  subtitle={[
                    `${fullName(domain.user)}${domain.user.username ? ` · @${domain.user.username}` : ''}`,
                    `${providerName(domain.provider)}${domain.providerRef ? ` · ${domain.providerRef}` : ''}`,
                    domain.expiresAt
                      ? `Действует до ${date(domain.expiresAt)}`
                      : `Создан ${dateTime(domain.createdAt)}`,
                    domain.autoRenew ? 'Автопродление включено' : 'Автопродление выключено',
                  ].join('\n')}
                  trailing={<StatusBadge map={DOMAIN_STATUS} status={domain.status} />}
                  chevron
                  onClick={() => nav.push({ name: 'adminUser', id: domain.user.id })}
                />
              ))}
            </ListGroup>
          </div>
          <Pager offset={offset} total={domains.data.total} onChange={setOffset} />
        </div>
      )}
    </main>
  );
}
